import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Building2, User, Globe, Server, Shield } from 'lucide-react';
import { useLanguage } from './LanguageContext';

const LegalPage = ({ onBack }) => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: "Mentions Légales",
      back: "Retour au site",
      sections: [
        {
          icon: Building2,
          title: "Éditeur du site",
          content: [
            { label: "Raison sociale", value: "ADAPEL" },
            { label: "Forme juridique", value: "SASU (Société par Actions Simplifiée Unipersonnelle)" },
            { label: "Capital social", value: "2 500,00 €" },
            { label: "SIREN", value: "919 146 522" },
            { label: "SIRET (siège)", value: "919 146 522 00017" },
            { label: "RCS", value: "Bobigny" },
            { label: "Numéro de TVA", value: "FR14919146522" },
            { label: "Adresse", value: "5 Rue de Bordeaux, 93800 Épinay-sur-Seine, France" },
            { label: "Activité", value: "Conseil pour les affaires et autres conseils de gestion (Code NAF 70.22Z)" },
            { label: "Date de création", value: "12 septembre 2022" },
            { label: "Directeur de la publication", value: "Hicham EL-HADY" },
            { label: "Contact", value: "contact@adapel.fr" }
          ]
        },
        {
          icon: Server,
          title: "Hébergement",
          content: [
            { label: "Hébergeur", value: "Hostinger International Ltd." },
            { label: "Adresse", value: "61 Lordou Vironos Street, 6023 Larnaca, Chypre" },
            { label: "Site web", value: "hostinger.fr" }
          ]
        },
        {
          icon: Shield,
          title: "Propriété intellectuelle",
          text: "L'ensemble du contenu de ce site (textes, images, graphismes, logo, icônes, etc.) est la propriété exclusive d'ADAPEL, à l'exception des marques, logos ou contenus appartenant à d'autres sociétés partenaires ou auteurs. Toute reproduction, distribution, modification, adaptation, retransmission ou publication de ces différents éléments est strictement interdite sans l'accord exprès par écrit d'ADAPEL."
        },
        {
          icon: Globe,
          title: "Données personnelles",
          text: "Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, vous disposez d'un droit d'accès, de rectification, de suppression et d'opposition aux données personnelles vous concernant. Pour exercer ces droits, vous pouvez nous contacter à l'adresse : contact@adapel.fr"
        }
      ],
      lastUpdate: "Dernière mise à jour : Juillet 2026"
    },
    en: {
      title: "Legal Notice",
      back: "Back to site",
      sections: [
        {
          icon: Building2,
          title: "Site Publisher",
          content: [
            { label: "Company name", value: "ADAPEL" },
            { label: "Legal form", value: "SASU (Simplified Joint-Stock Company, sole shareholder)" },
            { label: "Share capital", value: "€2,500.00" },
            { label: "SIREN", value: "919 146 522" },
            { label: "SIRET (headquarters)", value: "919 146 522 00017" },
            { label: "RCS", value: "Bobigny" },
            { label: "VAT number", value: "FR14919146522" },
            { label: "Address", value: "5 Rue de Bordeaux, 93800 Épinay-sur-Seine, France" },
            { label: "Activity", value: "Business consulting and other management consulting (NAF Code 70.22Z)" },
            { label: "Creation date", value: "September 12, 2022" },
            { label: "Publication Director", value: "Hicham EL-HADY" },
            { label: "Contact", value: "contact@adapel.fr" }
          ]
        },
        {
          icon: Server,
          title: "Hosting",
          content: [
            { label: "Host", value: "Hostinger International Ltd." },
            { label: "Address", value: "61 Lordou Vironos Street, 6023 Larnaca, Cyprus" },
            { label: "Website", value: "hostinger.com" }
          ]
        },
        {
          icon: Shield,
          title: "Intellectual Property",
          text: "All content on this site (texts, images, graphics, logos, icons, etc.) is the exclusive property of ADAPEL, except for trademarks, logos or content belonging to other partner companies or authors. Any reproduction, distribution, modification, adaptation, retransmission or publication of these elements is strictly prohibited without express written consent from ADAPEL."
        },
        {
          icon: Globe,
          title: "Personal Data",
          text: "In accordance with the General Data Protection Regulation (GDPR) and the French Data Protection Act, you have the right to access, rectify, delete and oppose your personal data. To exercise these rights, you can contact us at: contact@adapel.fr"
        }
      ],
      lastUpdate: "Last updated: July 2026"
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[var(--bg-primary)]/80 backdrop-blur-lg border-b border-[var(--border)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            {t.back}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pt-24 pb-16">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-12"
        >
          {t.title}
        </motion.h1>

        <div className="space-y-10">
          {t.sections.map((section, i) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-none p-8"
              >
                <h2 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <Icon size={24} className="text-[var(--primary)]" />
                  {section.title}
                </h2>
                
                {section.content && (
                  <dl className="space-y-3">
                    {section.content.map((item, j) => (
                      <div key={j} className="flex flex-col sm:flex-row sm:gap-4">
                        <dt className="text-[var(--text-muted)] sm:w-48 flex-shrink-0">{item.label}</dt>
                        <dd className="text-white font-medium">{item.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                
                {section.text && (
                  <p className="text-[var(--text-secondary)] leading-relaxed">
                    {section.text}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="text-[var(--text-muted)] text-sm mt-12 text-center">
          {t.lastUpdate}
        </p>
      </div>
    </div>
  );
};

export default LegalPage;
